/**
 * Settings sheet: volumes, motion, perf tier, subtitles, muted emote list, credits and legal links.
 *
 * Every control here writes the store and nothing else. Whatever has to act on a setting subscribes
 * to it: the audio engine through `TableScreen`'s volume ports, the scene through its own.
 */
import { useEffect, useState } from 'react';
import { apiPath } from '../../bridge/embedded';
import { selectMyLevel } from '../../store/selectors';
import { type MotionPref, type PerfTier, useStore } from '../../store/store';
import { seatDisplayName } from '../helpers';
import { leaveToMenu } from '../runtime';
import { Button, Segmented, Sheet, Slider, Toggle } from '../ui';
import { LevelBadge } from '../ui/LevelBadge';
import { SiteLink } from '../ui/SiteLink';
import { AccountSheet, useClaimOffer } from './Account';
import { CreditsSheet } from './Credits';
import { ProfileSheet } from './Profile';

const MOTION: ReadonlyArray<{ value: MotionPref; label: string }> = [
  { value: 'full', label: 'Full' },
  { value: 'reduced', label: 'Reduced' },
  { value: 'system', label: 'System' },
];
type PerfChoice = 'auto' | '0' | '1' | '2' | '3';
const PERF: ReadonlyArray<{ value: PerfChoice; label: string }> = [
  { value: 'auto', label: 'Auto' },
  { value: '0', label: 'Max' },
  { value: '1', label: 'High' },
  { value: '2', label: 'Mid' },
  { value: '3', label: 'Low' },
];

interface HealthBody {
  version?: string;
  uptime?: number;
  db?: string;
}

/**
 * Build identity, read from `/api/health` when the sheet opens.
 *
 * Worth a line in Settings because it is the first thing anyone asks for in a bug report, and a
 * player has no other way to tell which build they are on. Fetched lazily rather than at boot: it
 * is never needed until somebody opens this sheet, and it must never delay the table.
 */
export function About() {
  const [health, setHealth] = useState<HealthBody | null | 'error'>(null);

  useEffect(() => {
    let alive = true;
    fetch(apiPath('/api/health'))
      .then((r) => (r.ok ? (r.json() as Promise<HealthBody>) : Promise.reject(new Error(String(r.status)))))
      .then((h) => alive && setHealth(h))
      .catch(() => alive && setHealth('error'));
    return () => {
      alive = false;
    };
  }, []);

  const version = health && health !== 'error' ? (health.version ?? 'unknown') : health === 'error' ? 'offline' : '…';

  return (
    <div className="about small muted" data-testid="about">
      <div className="row row--between">
        <span>Build</span>
        <span className="tnum" data-testid="about-version">
          {version}
        </span>
      </div>
      {health && health !== 'error' && typeof health.uptime === 'number' && (
        <div className="row row--between">
          <span>Server up</span>
          <span className="tnum">{formatUptime(health.uptime)}</span>
        </div>
      )}
    </div>
  );
}

/** Whole units only: nobody reading a Settings sheet needs the seconds. */
export function formatUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const m = Math.floor(seconds / 60);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

export function SettingsSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const settings = useStore((s) => s.settings);
  const setSettings = useStore((s) => s.setSettings);
  const setVolume = useStore((s) => s.setVolume);
  const table = useStore((s) => s.table);
  const seat = useStore((s) => s.session.seat);
  const participants = useStore((s) => s.session.participants);
  const [credits, setCredits] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const myLevel = useStore(selectMyLevel);
  const platform = useStore((s) => s.session.platform);
  const myKind = useStore((s) => s.session.me?.kind);
  const claimOffer = useClaimOffer();
  // Web only. A signed-in player always gets the row, because Sign out lives behind it; a guest
  // only when the server can actually offer a way in.
  const showAccount = platform === 'web' && (myKind === 'discord' || myKind === 'email' || claimOffer);

  // Settings is the only surface a player can reach from every screen, so it is where "get me out
  // of this game" belongs. Hidden when there is no table: on the boot or entry screens there is
  // nothing to leave, and an inert danger button reads as a threat.
  const atTable = seat != null || table != null;

  // The confirm is armed per opening, not per session: a sheet reopened later must ask again.
  useEffect(() => {
    if (!open) setConfirmLeave(false);
  }, [open]);

  /**
   * Write the store and nothing else. The engine follows.
   *
   * This used to also call `audioHooks().setVolume(...)` directly. That hook only exists under
   * `?e2e=1`, so on a shipped build the direct call did nothing and the store write was the only
   * thing that happened — and since nothing was listening to the store, no slider ever reached the
   * engine in production. The audio layer now subscribes through `TableScreen`'s `subscribeVolumes`
   * port, which works on every build.
   *
   * The direct call is gone rather than left in as a belt-and-braces second path, because under
   * `?e2e=1` it drove the engine by itself and so masked the port failing: a spec asserting on the
   * engine's gain passed whether or not the port existed. A redundant path that only runs in tests
   * is worse than no path at all.
   */
  const vol = (which: 'master' | 'sfx' | 'music' | 'announcer') => (v: number) => {
    setVolume({ [which]: v });
  };

  const nameOf = (id: string) =>
    seatDisplayName(table?.seats.find((s) => s.playerId === id), '') || participants.find((p) => p.id === id)?.displayName || 'Player';

  return (
    <>
      <Sheet open={open} title="Settings" onClose={onClose} testId="settings">
        <div className="stack">
          {/* First, and above the volumes: Settings is the only sheet reachable from every screen,
              which makes it the one route to a player's own record while a hand is in play. */}
          <button type="button" className="setting-row" data-testid="open-profile" onClick={() => setProfileOpen(true)}>
            <LevelBadge level={myLevel} size={26} glow />
            <span className="setting-row__label">Your record</span>
            <span className="setting-row__chev" aria-hidden>
              ›
            </span>
          </button>
          {showAccount && (
            <button type="button" className="setting-row" data-testid="open-account" onClick={() => setAccountOpen(true)}>
              <span className="setting-row__label">Account</span>
              <span className="setting-row__value" data-testid="account-kind">
                {myKind === 'discord' ? 'Discord' : myKind === 'email' ? 'Email' : 'Guest · not saved'}
              </span>
              <span className="setting-row__chev" aria-hidden>
                ›
              </span>
            </button>
          )}

          <div className="eyebrow">Volume</div>
          <Slider label="Master" value={settings.volume.master} onChange={vol('master')} testId="vol-master" />
          <Slider label="Sound effects" value={settings.volume.sfx} onChange={vol('sfx')} testId="vol-sfx" />
          <Slider label="Music" value={settings.volume.music} onChange={vol('music')} testId="vol-music" />
          <Slider label="Announcer" value={settings.volume.announcer} onChange={vol('announcer')} testId="vol-announcer" />

          <div className="row row--between row--stack-sm">
            <div>
              <div>Motion</div>
              <div className="toggle__hint">Reduced: no shake, no particles</div>
            </div>
            <Segmented value={settings.motion} options={MOTION} onChange={(v) => setSettings({ motion: v })} label="Motion" testId="motion" />
          </div>
          <div className="row row--between row--stack-sm">
            <div>
              <div>Graphics</div>
              <div className="toggle__hint">Auto follows the frame rate</div>
            </div>
            <Segmented<PerfChoice>
              value={settings.perfOverride === 'auto' ? 'auto' : (String(settings.perfOverride) as PerfChoice)}
              options={PERF}
              onChange={(v) => setSettings({ perfOverride: v === 'auto' ? 'auto' : (Number(v) as PerfTier) })}
              label="Graphics quality"
              testId="perf"
            />
          </div>
          <Toggle label="Subtitles" hint="Announcer lines as text" checked={settings.subtitles} onChange={(v) => setSettings({ subtitles: v })} testId="subtitles" />

          {settings.mutedEmotes.length > 0 && (
            <div className="stack">
              <div className="eyebrow">Muted emotes</div>
              {settings.mutedEmotes.map((id) => (
                <div key={id} className="row row--between">
                  <span>{nameOf(id)}</span>
                  <Button variant="ghost" onClick={() => setSettings({ mutedEmotes: settings.mutedEmotes.filter((x) => x !== id) })}>
                    Unmute
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="eyebrow">About</div>
          <About />
          <div className="row row--wrap small">
            <Button variant="ghost" onClick={() => setCredits(true)} testId="open-credits">
              Credits
            </Button>
            {/* New tab: this sheet opens over a live table, and the site is a different page. */}
            <SiteLink className="link" path="/privacy" newTab>
              Privacy
            </SiteLink>
            <SiteLink className="link" path="/terms" newTab>
              Terms
            </SiteLink>
          </div>

          {atTable && (
            <div className="stack" data-testid="leave-game">
              {/* Last in the sheet and behind a confirm, because it is the one control here that
                  cannot be undone. The consequence is stated in the question rather than left for
                  the player to discover after they have pressed it. */}
              {confirmLeave ? (
                <>
                  <p className="small" style={{ margin: 0 }} data-testid="leave-confirm">
                    Leave the table? Your seat goes to a bot.
                  </p>
                  <div className="row row--wrap">
                    <Button
                      variant="danger"
                      testId="leave-confirm-yes"
                      onClick={() => {
                        setConfirmLeave(false);
                        onClose();
                        leaveToMenu();
                      }}
                    >
                      Leave
                    </Button>
                    <Button variant="ghost" testId="leave-confirm-no" onClick={() => setConfirmLeave(false)}>
                      Stay
                    </Button>
                  </div>
                </>
              ) : (
                <Button variant="danger" block testId="leave-game-start" onClick={() => setConfirmLeave(true)}>
                  Leave game
                </Button>
              )}
            </div>
          )}
        </div>
      </Sheet>
      <CreditsSheet open={credits} onClose={() => setCredits(false)} />
      <ProfileSheet open={profileOpen} onClose={() => setProfileOpen(false)} />
      <AccountSheet open={accountOpen} onClose={() => setAccountOpen(false)} where="settings" />
    </>
  );
}
